import type { ReactNode } from "react";
import { alpha, Box, ButtonBase, Collapse, Skeleton, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import type { DeductibleRow } from "./deductibleRows";

/** Visually hidden, still read by assistive tech (MUI has no exported helper for this). */
export const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

interface Props {
  row: DeductibleRow;
  icon: ReactNode;
  open: boolean;
  onToggle: () => void;
  /** Retry for an error placeholder row. */
  onRetry?: () => void;
  /** The expanded content; rendered only for an expandable row. */
  children?: ReactNode;
  last?: boolean;
}

/**
 * One rung of the deductible ladder (#7661): icon tile, title, one-line
 * subtitle, value on the right, chevron. A row with content is a button that
 * expands in place (`aria-expanded`); a row with nothing behind it has no
 * chevron and is not a button. The applied row's tile takes the primary tint
 * and carries a visually hidden "Applied" for screen readers.
 */
export const DeductibleDisclosureRow = ({ row, icon, open, onToggle, onRetry, children, last }: Props) => {
  const panelId = `deductible-row-${row.id.replace(/[^a-zA-Z0-9_-]/g, "-")}-panel`;
  const interactive = row.expandable && !row.status;

  const body = (
    <>
      <Box
        aria-hidden
        sx={(t) => ({
          width: 30,
          height: 30,
          borderRadius: 2,
          flex: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: row.applied ? alpha(t.palette.primary.main, 0.12) : t.palette.action.hover,
          color: row.applied ? t.palette.primary.main : t.palette.text.secondary,
          "& svg": { fontSize: 16 },
        })}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        {/* While loading, the title is a skeleton too: a placeholder title that is
            then replaced by the real rows read as a blink (Dillon, 2026-10-28). */}
        {row.status === "loading" ? (
          <Skeleton width={140} height={16} />
        ) : (
          <Typography component="div" sx={{ fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.3 }}>
            {row.title}
            {row.applied && (
              <Box component="span" sx={srOnly}>
                {" Applied"}
              </Box>
            )}
          </Typography>
        )}
        {row.status === "loading" ? (
          <Skeleton width={160} height={14} data-testid={`${row.id}-loading`} />
        ) : row.subtitle ? (
          <Typography
            component="div"
            sx={{ fontSize: "0.75rem", color: row.status === "error" ? "error.main" : "text.secondary", mt: 0.125 }}
            noWrap
          >
            {row.subtitle}
          </Typography>
        ) : null}
      </Box>
      {row.status === "error" && onRetry ? (
        <ButtonBase
          onClick={onRetry}
          sx={{ fontSize: "0.75rem", fontWeight: 600, color: "primary.main", px: 0.5, borderRadius: 1 }}
        >
          Retry
        </ButtonBase>
      ) : row.status === "loading" ? (
        <Skeleton width={64} height={14} />
      ) : row.value ? (
        <Typography
          component="div"
          sx={{
            fontSize: "0.8125rem",
            color: "text.secondary",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {row.value}
        </Typography>
      ) : null}
      <Box
        aria-hidden
        sx={{
          width: 16,
          display: "inline-flex",
          justifyContent: "center",
          color: "text.disabled",
          "& svg": { fontSize: 16 },
        }}
      >
        {interactive ? open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon /> : null}
      </Box>
    </>
  );

  const rowSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    width: "100%",
    px: 1.75,
    py: 1.5,
    textAlign: "left",
    font: "inherit",
  } as const;

  return (
    <Box
      data-testid={`deductible-row-${row.id}`}
      data-applied={row.applied ? "true" : "false"}
      sx={(t) => ({
        borderBottom: last && !open ? 0 : `1px solid ${t.palette.divider}`,
        "&:last-of-type": { borderBottom: 0 },
        bgcolor: open ? t.palette.action.hover : undefined,
      })}
    >
      {interactive ? (
        <ButtonBase
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          sx={{
            ...rowSx,
            "&:hover": { bgcolor: "action.hover" },
            "&.Mui-focusVisible": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
          }}
        >
          {body}
        </ButtonBase>
      ) : (
        <Box sx={rowSx}>{body}</Box>
      )}
      {interactive && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box id={panelId} role="region" aria-label={`${row.title} details`} sx={{ px: 1.75, pb: 1.75, pl: 7.25 }}>
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
